<template>
  <!-- container -->
  <div class="container mx-auto md:mx-0 px-5 md:w-2/3">
    <!-- sign in form -->
    <div class="flex flex-col h-screen justify-center items-center">
      <!-- logo -->
      <div
        class="flex items-center justify-center relative"
        style="height: 50%"
      >
        <div class="absolute bottom-0 mb-20 text-center w-80">
          <!-- RELE.AI image -->
          <div class="w-full flex justify-center">
            <div class="w-56">
              <img src="@/assets/icons/logo.svg" />
            </div>
          </div>

          <div class="flex flex-row relative">
            <!-- Info icon -->
            <rele-info-tooltip
              icon="info"
              @mouseover="onIconHover"
              @mouseleave="onIconLeave"
              class="absolute right-0 mr-12"
              :tooltip-html="$t('signup.info.tooltips.console')"
            />

            <!-- CONSOLE text -->
            <div class="mt-5 text-r-primary text-2xl tracking-widest w-80">
              {{ $t("signup.console") }}
            </div>
          </div>
        </div>
      </div>

      <!-- sign in options: google -->
      <div v-if="!isEmailPassVisible" style="height: 50%">
        <div class="flex items-center justify-center">
          <r-button
            id="sign-in-with-google-button"
            text
            class="flex item-center justify-center w-80 mb-3"
            @click="signInWithProvider('signInWithGoogle')"
          >
            <!-- icon -->
            <img src="@/assets/icons/google.png" class="w-5 mr-2" />

            <!-- content -->
            {{ $t("buttons.continueWithGoogle") }}
          </r-button>
        </div>

        <!-- sign in options: microsoft -->
        <div class="flex item-center justify-center mb-2">
          <r-button
            id="sign-in-with-microsoft-button"
            text
            class="flex item-center justify-center w-80 mb-3"
            @click="signInWithProvider('signInWithMicrosoft')"
          >
            <!-- icon -->
            <img src="@/assets/icons/microsoft.png" class="w-5 mr-2" />

            <!-- content -->
            {{ $t("buttons.continueWithMicrosoft") }}
          </r-button>
        </div>

        <!-- Divider -->
        <r-divider class="w-80 border-gray-400 mb-3">
          <span class="text-gray-400 text-base">
            {{ $t("globals.authDivider") }}
          </span>
        </r-divider>

        <!-- Sign in with email and pass link -->
        <div class="w-full flex justify-center">
          <span
            @click="toggleEmailAndPassVisibility"
            class="text-gray-600 text-base cursor-pointer"
          >
            {{ $t("buttons.signInWithEmailAndPassword") }}
          </span>
        </div>
      </div>

      <!-- sign in options: email and password -->
      <div
        v-else
        class="flex flex-row items-center text-blue-900 w-full relative"
        style="height: 50%"
      >
        <!-- back icon - md view only -->
        <div class="flex justify-end w-full h-full">
          <div
            @click="toggleEmailAndPassVisibility"
            class="flex justify-end cursor-pointer hidden md:block"
          >
            <r-icon
              icon="arrow-left"
              style="font-size: 45px"
              class="mt-8 mr-5"
            />
          </div>
        </div>

        <div
          v-if="!isVeraficationVisible"
          class="flex flex-col w-full h-full items-center"
        >
          <!-- email address input -->
          <r-text-field
            v-model="providedEmail"
            :style="stylePhoneInput"
            class="md:w-3/4 w-80"
            validation="email"
            :placeholder="$t('placeholders.enterYourEmail')"
            :label="$t('forms.email')"
          />

          <!-- sign in/up with email and password -->
          <div class="md:w-3/4 w-80 mt-3">
            <r-button
              text
              class="w-full flex items-center justify-center mt-5"
              :loading="loading"
              @click="signInWithEmailAndPassword"
            >
              {{ $t("buttons.continueWithEmail") }}
            </r-button>
          </div>
        </div>
        <!-- Verafication email sent -->
        <div v-else class="flex flex-col w-full h-full items-center mb-10">
          <!-- Verafication icon -->
          <font-awesome-icon icon="paper-plane" size="5x" />

          <div class="text-center w-80 md:w-full">
            <!-- Verafication title -->
            <div class="mt-8 font-medium text-xl">
              {{ $t("signup.veraficationEmailSentTitle") }}
            </div>

            <!-- Verafication description -->
            <div class="mt-4">
              {{ $t("signup.veraficationEmailSentDesc") }}
            </div>
          </div>
        </div>
        <div class="w-full"></div>
      </div>
    </div>

    <!-- right side of the screen with the image -->
    <div
      :style="{ backgroundImage: `url(${bgUrl})` }"
      class="hidden md:block h-screen w-1/3 absolute top-0 right-0"
    />
  </div>
</template>

<script>
import bgUrl from "@/assets/images/background-img.png"

/**
 * Define the signup page
 */
export default {
  // page state
  data() {
    return {
      bgUrl,
      loading: false,
      providedEmail: "",

      // email and password visibility
      isEmailPassVisible: false,

      // infoInfoActive
      infoInfoActive: false,

      // isVeraficationVisible
      isVeraficationVisible: false
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // computed props
  computed: {
    /**
     * stylePhoneInput returns the phone input style
     */
    stylePhoneInput() {
      if (this.infoInfoActive) {
        return { "z-index": -10 }
      }

      return { "z-index": 0 }
    }
  },

  // component functions
  methods: {
    /**
     * On sign in with email and pass click
     */
    toggleEmailAndPassVisibility() {
      this.isVeraficationVisible = false
      this.providedEmail = ""
      this.loading = false
      this.isEmailPassVisible = !this.isEmailPassVisible
    },

    /**
     * Sign in with email and password
     */
    async signInWithEmailAndPassword() {
      this.loading = true

      try {
        // send email link to sign in
        await this.$store.dispatch("auth/continueWithEmailAndPassword", {
          email: this.providedEmail
        })

        // set verafication alert
        this.isVeraficationVisible = true
      } catch (error) {
        this.notifyError({
          title: this.$t("errors.unableToAuthenticateTitle"),
          message: error.message
        })
      }
    },

    /**
     * Sign in with custome provider
     */
    signInWithProvider(action) {
      return this.$store.dispatch(`auth/${action}`).catch((error) => {
        this.notifyError({
          title: this.$t("errors.unableToAuthenticateTitle"),
          message: error.message
        })
      })
    },

    /**
     * On info icon hover
     */
    onIconHover() {
      this.infoInfoActive = true
    },

    /**
     * On info icon leave
     */
    onIconLeave() {
      this.infoInfoActive = false
    }
  }
}
</script>
